import { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import { COACHES } from '../../lib/coaches';
import { Pricing } from './Pricing';
import { fadeInUp } from '../../lib/animations';
import { staggerContainer } from '../../lib/animations';
import { useLanguage } from '../../context/LanguageContext';

export const Trainers = () => {
  const { t, language } = useLanguage();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const trainers = COACHES.map(c => ({
    slug: c.slug,
    name: c.name,
    image: c.image,
    title: t.trainers[c.titleKey],
    experience: t.trainers[c.experienceKey],
    specialty: t.trainers[c.specialtyKey]
  }));

  return (
    <section id="trainers" ref={ref} className="py-32 bg-slate-50" data-testid="trainers-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <p className="overline mb-4">{t.trainers.meet.toUpperCase()}</p>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 tracking-tight">
            {t.trainers.title}
          </h2>
          <p className="text-xl text-slate-600">
            {t.trainers.subtitle}
          </p>
        </motion.div>

        <motion.div 
          variants={staggerContainer}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid grid-cols-1 md:grid-cols-3 gap-8"
        >
          {trainers.map((trainer, index) => (
            <motion.div
              key={index}
              variants={fadeInUp}
              className="minimal-card rounded-3xl overflow-hidden group cursor-pointer"
            >
              <Link to={`/coach/${trainer.slug}`} className="block" data-testid={`coach-card-${trainer.slug}`}>
                <div className="relative h-96 overflow-hidden">
                  <img 
                    src={trainer.image} 
                    alt={trainer.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/40 to-transparent"></div>
                  <div className="absolute top-4 right-4 px-3 py-1.5 bg-white/90 backdrop-blur-sm rounded-full text-[10px] font-bold uppercase tracking-wider text-slate-900 opacity-0 group-hover:opacity-100 transition-opacity">
                    {t.trainers.viewBio} →
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="text-2xl font-bold mb-2">{trainer.name}</h3>
                    <p className="text-blue-300 font-semibold text-sm mb-3">{trainer.title}</p>
                  </div>
                </div>
                <div className="p-6 bg-white">
                  <div className="flex justify-between items-center gap-3 mb-2 pb-2 border-b border-slate-100">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{t.trainers.experience}</span>
                    <span className="text-sm font-bold text-slate-900 text-right">{trainer.experience}</span>
                  </div>
                  <div className="flex justify-between items-center gap-3">
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wider whitespace-nowrap">{t.trainers.specialty}</span>
                    <span className="text-sm font-bold text-slate-900 text-right">{trainer.specialty}</span>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

// Pricing Section
